/** @format */
import React, { useContext, useState } from 'react';

import { toSnakeCase } from '@toucan/common/helpers/change-case';
import { SearchFacet } from '@toucan/common/services/search/constants';
import { getModelListingQuery } from '@toucan/common/services/search/model/queries';
import { Facet } from '@toucan/common/services/search/types';

import { MarketContext } from '@toucan/client/global/market';

import {
    getListingResults,
    getSemanticListingResults,
} from '@toucan/client/core/search';

import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';
import { useShouldUseSemanticSearch } from '@toucan/client/features/search/components/model-search-results/hooks/use-should-use-semantic-search';

import { InlineFilters } from '../filters-inline';
import { ModalFilters } from '../filters-modal';
import { StyledFilters } from './filters.style';
import { useFacetsQuery } from './hooks/use-facets-query';
import { useFacetsToFilters } from './hooks/use-facets-to-filters';
import { FiltersProps } from './types';

export function Filters({
    baseQuery,
    searchResults,
    disallowedFilters,
    testId,
    className,
    type,
}: FiltersProps) {
    const { activeMarket } = useContext(MarketContext);
    const searchParams = useSearchParams();
    const shouldUseSemanticSearch = useShouldUseSemanticSearch(type);

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const { sort, outOfStock, selectedFilters } = searchParams;
    const [openedFilter, setOpenedFilter] = useState<Facet | null>(null);

    const { facets, isLoading } = useFacetsQuery(
        baseQuery,
        disallowedFilters,
        sort,
        activeMarket,
        selectedFilters,
        outOfStock,
        type
    );

    const filters = useFacetsToFilters(facets, outOfStock, openedFilter);

    async function handleFilterOpen(id: SearchFacet): Promise<void> {
        const facetFilterQuery = { ...selectedFilters };
        delete facetFilterQuery[id];

        const getListingRequest = shouldUseSemanticSearch
            ? getSemanticListingResults
            : getListingResults;

        const { facets: updatedFacets } = await getListingRequest(
            getModelListingQuery(
                baseQuery,
                1,
                0,
                facetFilterQuery,
                sort,
                activeMarket.code,
                [toSnakeCase(id)]
            ),
            activeMarket.iso,
            activeMarket.code
        );

        setOpenedFilter(updatedFacets[0]);
    }

    return (
        <StyledFilters className={className}>
            <InlineFilters
                testId={`${testId}__filters-inline`}
                filters={filters}
                isLoadingFilters={isLoading}
                handleFilterOpen={handleFilterOpen}
            />
            <ModalFilters
                testId={`${testId}__filters-modal`}
                filters={filters}
                isLoadingFilters={isLoading}
                handleFilterOpen={handleFilterOpen}
                searchResults={searchResults}
            />
        </StyledFilters>
    );
}
